import React, { useEffect, useMemo, useState } from "react";
import { t } from "i18next";
import { uuid } from "@/utils/string";
import { useAgenda } from "@/contexts/AgendaContext.tsx";
import { useUserEventsAndMaterials } from "@/data/user.ts";
import { generateMonthlySlots, getDateTruncated, getMonthlyMoment } from "@/utils/agenda.ts";
import moment from "moment-timezone";
import DateItem from "@/components/agenda/desktop/month/DateItem.tsx";
import { useTimezone } from "@/contexts/TimezoneContext";

const MonthView: React.FC = () => {
  const { selectedTimezone } = useTimezone();
  const { eventMaterials } = useUserEventsAndMaterials();
  const { index } = useAgenda();
  const [dataLoaded, setDataLoaded] = useState(false);

  // Memoize the current moment in the selected timezone
  const currentMoment = useMemo(() => {
    return getMonthlyMoment(0, selectedTimezone);
  }, [selectedTimezone]);

  // Memoize events with timezone conversion
  const events = useMemo(() => {
    return generateMonthlySlots(eventMaterials ? eventMaterials.item.events : [], selectedTimezone);
  }, [eventMaterials, selectedTimezone]);

  // Memoize the truncated dates
  const truncatedDates = useMemo(() => {
    const momentForIndex = getMonthlyMoment(index, selectedTimezone);
    return getDateTruncated(momentForIndex).flat();
  }, [index, selectedTimezone]);

  const WeekMap = useMemo(() => {
    return truncatedDates.map((day) => {
      let type: "past" | "now" | "future" = "now";
      // Create the moment in the target timezone directly
      const m = moment.tz(day, "YYYY-MM-DD", selectedTimezone);

      if (m.isBefore(currentMoment, "month")) {
        type = "past";
      }

      if (m.isAfter(currentMoment, "month")) {
        type = "future";
      }

      return (
        <DateItem
          key={day}
          id={m.format("YYYY-MM-DD")}
          type={type}
          value={m}
          events={events ? events : []}
        />
      );
    });
  }, [events, truncatedDates, selectedTimezone, currentMoment]);

  useEffect(() => {
    if (events) {
      setDataLoaded(true);
    }
  }, [events]);

  useEffect(() => {
    if (dataLoaded) {
      const currentDayElement = document.getElementById(
        currentMoment.format("YYYY-MM-DD")
      );
      if (currentDayElement) {
        currentDayElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }
  }, [dataLoaded, currentMoment]); 

  return (
    <div className="flex flex-col h-full">
      <div className="grid grid-cols-7 mb-2 w-full">
        {Array.from({ length: 7 }).map((_, i) => (
          <p
            className="uppercase text-white text-center text-xs font-semibold"
            key={uuid()}
          >
            {t(`weeks.short.${i + 1}`)}
          </p>
        ))}
      </div>

      <div className="flex-col flex h-full my_scroll pr-2 pb-2 overflow-y-auto max-h-[calc(100%_-_32px)]">
        <div className="grid grid-cols-7 gap-1 w-full auto-cols-auto h-full">
          {WeekMap}
        </div>
      </div>
    </div>
  );
};

export default MonthView;